import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  IconButton,
  Chip,
  Alert,
  Grid,
  Divider,
  Button,
  Tooltip
} from '@mui/material';
import { 
  ExpandLess, 
  ExpandMore,
  VolumeUp as VolumeUpIcon,
  Vibration as VibrationIcon,
  PlayArrow,
  Stop,
  Delete,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { autoJsStoreUtils, autoWebViewJs } from '@droid/android';
import { useProxyWatch } from '@fanfanlo';
import {user} from '@app/model';
import _ from 'lodash';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SHAKE_CONFIG_KEY = "shake_emergency_config"

// 阈值范围常量
const THRESHOLD_MIN = 7;
const THRESHOLD_MAX = 15;
const THRESHOLD_DEFAULT = 9;
// 预报警动作接口
interface PreAlarmAction {
  id: string;
  type: 'sound' | 'vibration';
  duration: number;
  resourceId?: string;
}

// 摇一摇配置接口定义
interface ShakeConfig {
  enabled: boolean;
  shakeCount: number;
  timeWindowMs: number;
  emergencyType: string;
  threshold: number;
  vibrationFeedback: boolean;
  soundFeedback: boolean;
  actions: PreAlarmAction[];
  maxWaitDuration: number;
  version: number;
}

// 默认配置
const defaultConfig: ShakeConfig = {
  enabled: false,
  shakeCount: 3,
  timeWindowMs: 2000,
  emergencyType: "medical",
  threshold: THRESHOLD_DEFAULT,
  vibrationFeedback: true,
  soundFeedback: false,
  actions: [],
  maxWaitDuration: 10000,
  version: 1
};

// 配置选项定义
const emergencyTypeOptions = [
  { value: "medical", label: "医疗报警" },
  { value: "security", label: "安全报警" }
];

// 默认声音资源
const defaultSoundResources = [
  { id: 'siren_gentle', name: '缓和警报' },
  { id: 'beep_short', name: '短提示音' },
  { id: 'alarm_clock', name: '闹钟' },
];


function loadConfig():ShakeConfig{
  const conf = autoJsStoreUtils.read(SHAKE_CONFIG_KEY,{})
  const config = _.merge(defaultConfig, conf)
  return config;
}
const ShakeConfigUI: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [config, setConfig] = useState<ShakeConfig>(defaultConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSaveTime, setLastSaveTime] = useState<string>('');
  const [playingSoundId, setPlayingSoundId] = useState<string | null>(null);
  
  // 拖拽传感器配置
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // 检测用户登录状态
  const [userToken] = useProxyWatch(user, 'data.storeData.user_token', user.data.storeData.user_token);
  const isLoggedIn = !!userToken;
  
  // 临时输入值状态，用于处理输入验证
  const [tempValues, setTempValues] = useState<{
    shakeCount?: string;
    timeWindowMs?: string;
    threshold?: string;
  }>({});

  /**
   * 从Android端读取当前配置
   */
  const loadConfigFromAndroid = useCallback(() => {
    console.log('尝试从Android端读取摇一摇配置...');
    const conf = loadConfig();
    console.log(SHAKE_CONFIG_KEY, 'string', JSON.stringify(conf))
    setConfig(conf)
      setIsLoading(false);
  }, []);

  /**
   * 验证配置参数的有效性
   */
  const validateConfig = (config: ShakeConfig): boolean => {
    return (
      config.shakeCount >= 1 && config.shakeCount <= 10 &&
      config.timeWindowMs > 0 && config.timeWindowMs <= 10000 &&
      ['medical', 'security'].includes(config.emergencyType) &&
      typeof config.threshold === 'number' &&
      config.threshold >= 0 &&
      config.version > 0
    );
  };

  /**
   * 保存配置到Android端
   */
  const saveConfigToAndroid = useCallback((newConfig: ShakeConfig) => {
    try {
      console.log('保存摇一摇配置到Android端:', newConfig);
      
      // 验证配置有效性
      if (!validateConfig(newConfig)) {
        console.error('配置参数无效，不进行保存');
        return;
      }

      // 构建配置JSON
      const configJson = JSON.stringify(newConfig);
      
      // 编码为Base64
      const base64Config = btoa(unescape(encodeURIComponent(configJson)));
      
      // 通过Rhino调用Android方法
      const script = `
        
          var result = com.fanfanlo.emergencycall.controller.ShakeManager.updateShakeConfig("${base64Config}");
          result;
      `;
      autoWebViewJs.callScript(script);
      setLastSaveTime(new Date().toLocaleTimeString());
      
    } catch (error) {
      console.error('保存摇一摇配置失败:', error);
    }
  }, []);

  /**
   * 处理配置更新
   */
  const updateConfig = useCallback((updates: Partial<ShakeConfig>) => {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);
    
    // 如果配置有效，自动保存
    if (validateConfig(newConfig)) {
      saveConfigToAndroid(newConfig);
    }
  }, [config, saveConfigToAndroid]);

  /**
   * 处理数字输入框变更，带延迟验证
   */
  const handleNumberInput = useCallback((field: 'shakeCount' | 'timeWindowMs' | 'threshold', value: string) => {
    // 更新临时值
    setTempValues(prev => ({ ...prev, [field]: value }));
    
    // 清除之前的定时器
    const timerId = setTimeout(() => {
      if (field === 'threshold') {
        // 处理阈值输入
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue >= 0) {
          updateConfig({ [field]: numValue });
          setTempValues(prev => ({ ...prev, [field]: undefined }));
        } else if (value === '') {
          // 空值，等待用户继续输入
          setTimeout(() => {
            if (tempValues[field] === '') {
              setTempValues(prev => ({ ...prev, [field]: undefined }));
            }
          }, 2000);
        } else {
          // 无效值，恢复原值
          setTimeout(() => {
            setTempValues(prev => ({ ...prev, [field]: undefined }));
          }, 2000);
        }
      } else {
        // 原有的整数处理逻辑保持不变
        const numValue = parseInt(value);
        if (!isNaN(numValue) && numValue > 0) {
          if (field === 'shakeCount' && numValue >= 1 && numValue <= 10) {
            updateConfig({ [field]: numValue });
            setTempValues(prev => ({ ...prev, [field]: undefined }));
          } else if (field === 'timeWindowMs' && numValue >= 1000 && numValue <= 10000) {
            updateConfig({ [field]: numValue });
            setTempValues(prev => ({ ...prev, [field]: undefined }));
          } else {
            setTimeout(() => {
              setTempValues(prev => ({ ...prev, [field]: undefined }));
            }, 2000);
          }
        } else if (value === '') {
          setTimeout(() => {
            if (tempValues[field] === '') {
              setTempValues(prev => ({ ...prev, [field]: undefined }));
            }
          }, 2000);
        } else {
          setTimeout(() => {
            setTempValues(prev => ({ ...prev, [field]: undefined }));
          }, 2000);
        }
      }
    }, 500); // 500ms延迟

    return () => clearTimeout(timerId);
  }, [updateConfig, tempValues]);

  /**
   * 预报警动作管理函数
   */
  const handleAddPreAlarmAction = useCallback((type: 'sound' | 'vibration') => {
    const newAction: PreAlarmAction = {
      id: `action-${Date.now()}`,
      type,
      duration: type === 'vibration' ? 1000 : 2000,
      ...(type === 'sound' && { resourceId: defaultSoundResources[0].id }),
    };
    updateConfig({
      actions: [...config.actions, newAction],
    });
  }, [config.actions, updateConfig]);

  const handleUpdatePreAlarmAction = useCallback((id: string, updates: Partial<Omit<PreAlarmAction, 'id'>>) => {
    updateConfig({
      actions: config.actions.map((action) =>
        action.id === id ? { ...action, ...updates } : action
      ),
    });
  }, [config.actions, updateConfig]);

  const handleRemovePreAlarmAction = useCallback((id: string) => {
    updateConfig({
      actions: config.actions.filter((action) => action.id !== id),
    });
  }, [config.actions, updateConfig]);

  const handlePreviewSound = useCallback((resourceId?: string) => {
    if (!resourceId) return;
    handleStopSound();
    
    console.log(`[ShakeConfig] Previewing sound: ${resourceId}`);
    
    if ((window as any).SoundManager) {
      (window as any).SoundManager.previewSound(resourceId);
      setPlayingSoundId(resourceId);
    } else {
      console.warn('SoundManager not available');
    }
  }, []);

  const handleStopSound = useCallback(() => {
    if ((window as any).SoundManager) {
      (window as any).SoundManager.stopPreview();
    }
    setPlayingSoundId(null);
  }, []);

  /**
   * 拖拽结束处理
   */
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = config.actions.findIndex((a) => a.id === active.id);
    const newIndex = config.actions.findIndex((a) => a.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      updateConfig({
        actions: arrayMove(config.actions, oldIndex, newIndex),
      });
    }
  }, [config.actions, updateConfig]);

  /**
   * 可拖拽的预报警动作项组件
   */
  const SortablePreAlarmActionItem: React.FC<{
    action: PreAlarmAction;
    isPlaying: boolean;
    onUpdate: (id: string, updates: Partial<Omit<PreAlarmAction, 'id'>>) => void;
    onRemove: (id: string) => void;
    onPreview: (resourceId?: string) => void;
    onStopPreview: () => void;
    disabled: boolean;
  }> = ({ action, isPlaying, onUpdate, onRemove, onPreview, onStopPreview, disabled }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: action.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.8 : 1,
      zIndex: isDragging ? 1 : 'auto',
    };

    return (
      <Paper
        ref={setNodeRef}
        style={style}
        elevation={isDragging ? 3 : 1}
        sx={{
          p: 2,
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          userSelect: 'none',
          opacity: disabled ? 0.6 : 1
        }}
      >
        <Box {...attributes} {...listeners} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', touchAction: 'none' }}>
          <DragIndicatorIcon />
        </Box>
        
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
            <Typography variant="subtitle2">
              {action.type === 'sound' ? '声音' : '震动'}
            </Typography>
            
            <TextField
              size="small"
              type="number"
              label="持续时间 (ms)"
              value={action.duration}
              onChange={(e) => onUpdate(action.id, { duration: parseInt(e.target.value, 10) || 0 })}
              disabled={disabled}
              sx={{ width: '100%' }}
            />
            
            {action.type === 'sound' && (
              <FormControl size="small" sx={{ width: '100%' }}>
                <InputLabel>声音类型</InputLabel>
                <Select
                  value={action.resourceId || ''}
                  label="声音类型"
                  onChange={(e) => onUpdate(action.id, { resourceId: e.target.value })}
                  disabled={disabled}
                  startAdornment={
                    <Tooltip title={isPlaying ? "停止" : "试听"}>
                      <IconButton
                        size="small"
                        onClick={() => isPlaying ? onStopPreview() : onPreview(action.resourceId)}
                        sx={{ mr: 1 }}
                        disabled={disabled}
                      >
                        {isPlaying ? <Stop fontSize="small" /> : <PlayArrow fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  }
                >
                  {defaultSoundResources.map((sound) => (
                    <MenuItem key={sound.id} value={sound.id}>
                      {sound.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
        
        <IconButton onClick={() => onRemove(action.id)} size="small" color="error" disabled={disabled}>
          <Delete />
        </IconButton>
      </Paper>
    );
  };

  /**
   * 监听console.log消息以接收Android端的响应
   */
  useEffect(() => {
    const originalConsoleLog = console.log;
    
    console.log = (...args) => {
      const message = args.join(' ');
      
      // 处理配置加载响应
      if (message.startsWith('SHAKE_CONFIG_LOADED:')) {
        const configData = message.replace('SHAKE_CONFIG_LOADED:', '');
        if (configData === 'null') {
          console.log('未找到摇一摇配置，使用默认配置');
          setConfig(defaultConfig);
        } else {
          try {
            const loadedConfig = JSON.parse(configData);
            setConfig(loadedConfig);
            console.log('摇一摇配置加载成功:', loadedConfig);
          } catch (error) {
            console.error('解析配置失败:', error);
            setConfig(defaultConfig);
          }
        }
        setIsLoading(false);
      }
      
      // 处理保存结果响应
      else if (message.startsWith('SHAKE_CONFIG_SAVE_RESULT:')) {
        const result = message.replace('SHAKE_CONFIG_SAVE_RESULT:', '');
        console.log('配置保存结果:', result === 'true' ? '成功' : '失败');
      }
      
      // 处理错误响应
      else if (message.startsWith('SHAKE_CONFIG_ERROR:') || message.startsWith('SHAKE_CONFIG_SAVE_ERROR:')) {
        console.error('摇一摇配置操作失败:', message);
        setIsLoading(false);
      }
      
      // 调用原始的console.log
      originalConsoleLog.apply(console, args);
    };

    // 组件挂载时加载配置
    loadConfigFromAndroid();

    // 清理函数
    return () => {
      console.log = originalConsoleLog;
    };
  }, [loadConfigFromAndroid]);

  // 渲染加载状态
  if (isLoading) {
    return (
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            加载摇一摇配置...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* 折叠标题栏 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer',
          opacity: !isLoggedIn ? 0.5 : 1,
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            摇一摇
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled}
                onChange={(e) => updateConfig({ enabled: e.target.checked })}
                size="small"
                disabled={!isLoggedIn}
              />
            }
            label={config.enabled ? '已启用' : '已禁用'}
            onClick={(e) => e.stopPropagation()} // 防止点击开关时触发展开/折叠
          />
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastSaveTime && (
            <Typography variant="caption" color="text.secondary">
              已保存 {lastSaveTime}
            </Typography>
          )}
          <IconButton size="small">
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      {/* 展开的配置界面 */}
      <Collapse in={isExpanded}>
        <Divider />
        <Box sx={{ p: 2, opacity: !isLoggedIn ? 0.6 : 1, pointerEvents: !isLoggedIn ? 'none' : 'auto' }}>
          {/* 未登录提示 */}
          {/* {!isLoggedIn && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                请先登录后再使用摇一摇功能
              </Typography>
            </Alert>
          )} */}
          {/* 基础设置 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* 报警类型 */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!config.enabled || !isLoggedIn}>
                <InputLabel>报警类型</InputLabel>
                <Select
                  value={config.emergencyType}
                  label="报警类型"
                  onChange={(e) => updateConfig({ emergencyType: e.target.value })}
                >
                  {emergencyTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* 检测参数 */}
          <Typography variant="h6" gutterBottom>检测参数</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* 摇动次数 */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="摇动次数"
                type="number"
                inputProps={{ min: 1, max: 10 }}
                value={tempValues.shakeCount !== undefined ? tempValues.shakeCount : config.shakeCount}
                onChange={(e) => handleNumberInput('shakeCount', e.target.value)}
                disabled={!config.enabled || !isLoggedIn}
                helperText="1-10次"
                error={tempValues.shakeCount !== undefined && (tempValues.shakeCount === '' || parseInt(tempValues.shakeCount) < 1 || parseInt(tempValues.shakeCount) > 10)}
              />
            </Grid>

            {/* 时间窗口 */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="时间窗口(毫秒)"
                type="number"
                inputProps={{ min: 1000, max: 10000, step: 500 }}
                value={tempValues.timeWindowMs !== undefined ? tempValues.timeWindowMs : config.timeWindowMs}
                onChange={(e) => handleNumberInput('timeWindowMs', e.target.value)}
                disabled={!config.enabled || !isLoggedIn}
                helperText="1000-10000ms"
                error={tempValues.timeWindowMs !== undefined && (tempValues.timeWindowMs === '' || parseInt(tempValues.timeWindowMs) < 1000 || parseInt(tempValues.timeWindowMs) > 10000)}
              />
            </Grid>

            {/* 检测阈值 */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="检测阈值"
                type="number"
                inputProps={{ min: 0, step: 0.1 }}
                value={tempValues.threshold !== undefined ? tempValues.threshold : config.threshold}
                onChange={(e) => handleNumberInput('threshold', e.target.value)}
                disabled={!config.enabled || !isLoggedIn}
                helperText={`数值越小越敏感，一般范围为${THRESHOLD_MIN}-${THRESHOLD_MAX}，根据手机不同会有差异`}
                error={tempValues.threshold !== undefined && (tempValues.threshold === '' || parseFloat(tempValues.threshold) < 0)}
              />
            </Grid>
          </Grid>

          {/* 预警设置 */}
          <Typography variant="h6" gutterBottom>预警设置</Typography>
          <Box sx={{ mb: 3 }}>
            {/* 预报警详细配置 */}
            <Collapse in={config.actions.length > 0 || true}>
              <Box sx={{ mt: 2, pl: 4, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="预警最大等待时间(ms)"
                      value={config.maxWaitDuration}
                      onChange={(e) => updateConfig({ maxWaitDuration: parseInt(e.target.value, 10) || 0 })}
                      disabled={!config.enabled || !isLoggedIn}
                      helperText="用户可在此时间内确认或取消"
                    />
                  </Grid>
                </Grid>
                
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      预警动作序列 ({config.actions.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VolumeUpIcon />}
                        onClick={() => handleAddPreAlarmAction('sound')}
                        disabled={!config.enabled || !isLoggedIn}
                      >
                        添加声音
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VibrationIcon />}
                        onClick={() => handleAddPreAlarmAction('vibration')}
                        disabled={!config.enabled || !isLoggedIn}
                      >
                        添加震动
                      </Button>
                    </Box>
                  </Box>
                  
                  {config.actions.length === 0 ? (
                    <Box sx={{ 
                      p: 2, 
                      border: '1px dashed', 
                      borderColor: 'divider', 
                      borderRadius: 1, 
                      textAlign: 'center',
                      color: 'text.secondary'
                    }}>
                      <Typography variant="body2">暂无预警动作，点击上方按钮添加</Typography>
                    </Box>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={config.actions.map((a) => a.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {config.actions.map((action) => (
                          <SortablePreAlarmActionItem
                            key={action.id}
                            action={action}
                            isPlaying={playingSoundId === action.resourceId}
                            onUpdate={handleUpdatePreAlarmAction}
                            onRemove={handleRemovePreAlarmAction}
                            onPreview={handlePreviewSound}
                            onStopPreview={handleStopSound}
                            disabled={!config.enabled || !isLoggedIn}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </Box>
              </Box>
            </Collapse>
            
            {/* 基础反馈设置 */}
            <FormControlLabel
              control={
                <Switch
                  checked={config.vibrationFeedback}
                  onChange={(e) => updateConfig({ vibrationFeedback: e.target.checked })}
                  disabled={!config.enabled || !isLoggedIn}
                />
              }
              label="震动反馈"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={config.soundFeedback}
                  onChange={(e) => updateConfig({ soundFeedback: e.target.checked })}
                  disabled={!config.enabled || !isLoggedIn}
                />
              }
              label="声音反馈"
            />
          </Box>

          {/* 配置说明 */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" component="div">
              <strong>配置说明</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>在{config.timeWindowMs}毫秒内连续摇动{config.shakeCount}次将触发报警</li>
                <li>{config.emergencyType === 'medical' ? '医疗报警适合健康紧急情况' : '安全报警适合人身安全威胁'}</li>
                <li>阈值{config.threshold}：数值越小检测越敏感，一般范围为{THRESHOLD_MIN}-{THRESHOLD_MAX}，根据手机不同会有差异</li>
                <li>{config.actions.length > 0 ? `启用预警将在${config.maxWaitDuration}毫秒内执行${config.actions.length}个预警动作，用户可确认或取消` : '无预警动作配置，将直接触发报警'}</li>
              </ul>
            </Typography>
          </Alert>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default ShakeConfigUI;